import { CalendarCheckIcon, MailIcon, MapPinIcon, PhoneCallIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { appConfig } from 'shared';
import type { TKey } from '~/lib/i18n-locales';
import { ContactForm } from '~/modules/common/contact-form/contact-form';
import type { IconComponent } from '~/modules/common/icons/types';
import { ExternalArrow } from '~/modules/marketing/external-arrow';
import { MarketingLayout } from '~/modules/marketing/layout';

interface ContactMethod {
  icon: IconComponent;
  title: TKey;
  link: string;
  /** Link text as a translation key; wins over `text`. */
  textKey?: TKey;
  text?: string;
}

const methods: ContactMethod[] = [
  { icon: MapPinIcon, title: 'c:visit', link: appConfig.company.directionsUrl, text: appConfig.company.streetAddress },
  { icon: MailIcon, title: 'c:email', link: `mailto:${appConfig.company.email}`, text: appConfig.company.email },
];

if (appConfig.company.scheduleCallUrl)
  methods.push({ icon: CalendarCheckIcon, title: 'c:book', link: appConfig.company.scheduleCallUrl, textKey: 'c:schedule_call' });
if (appConfig.company.tel) methods.push({ icon: PhoneCallIcon, title: 'c:call', link: `tel:${appConfig.company.tel}`, text: appConfig.company.tel });

export function ContactPage() {
  const { t } = useTranslation();

  return (
    <MarketingLayout title="c:contact_us">
      <div className="container pt-20 pb-16">
        <h2 className="mb-4 text-center text-3xl font-semibold sm:text-left md:text-4xl">{t('c:leave_message')}</h2>
        <p className="text-muted-foreground mb-8 text-center sm:text-left sm:text-lg">{t('c:contact_us.text')}</p>
        <ContactForm />
      </div>
      <div className="container mb-12">
        <div className="flex flex-wrap justify-evenly gap-2">
          {methods.map((method) => {
            // The mail and phone links hand over to an app, which needs no tab
            const isWebPage = method.link.startsWith('http');
            return (
              <div key={method.title} className="mb-10 h-48 w-40 text-center sm:w-48">
                <div className="bg-accent/50 text-primary mx-auto mb-5 flex size-24 items-center justify-center rounded-full sm:size-32">
                  <method.icon className="size-12" strokeWidth={1} />
                </div>
                <h3 className="mb-3 text-lg font-semibold">{t(method.title)}</h3>
                <p>
                  <a
                    href={method.link}
                    className="group/external-link focus-effect link-inline rounded-md p-0.5 text-sm sm:text-base"
                    target={isWebPage ? '_blank' : undefined}
                    rel={isWebPage ? 'noreferrer' : undefined}
                  >
                    {method.textKey ? t(method.textKey) : method.text}
                    <ExternalArrow />
                  </a>
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </MarketingLayout>
  );
}
